<template>
  <div class="pds__table-wrap">
    <div class="pds__table-head" style="flex-wrap: wrap; gap: 12px;">
      <div v-if="title" style="flex: 1; min-width: 250px;">
        <h3>{{ title }}</h3>
        <p v-if="subtitle">{{ subtitle }}</p>
      </div>
      <div :style="title ? 'display: flex; align-items: center; gap: 8px; flex-wrap: wrap;' : 'display: flex; align-items: center; justify-content: flex-end; width: 100%; gap: 8px; flex-wrap: wrap;'">
        <!-- Filtros Dinámicos (DashboardSelect) -->
        <div v-for="col in computedFilterableColumns" :key="col.key" class="pd-table-filter" style="min-width: 150px;">
          <DashboardSelect
            v-model="internalFilters[col.key]"
            :options="col.options"
            :placeholder="col.label"
          />
        </div>
        
        <div class="pd-table-search" v-if="showSearch">
           <i class="fa-solid fa-magnifying-glass"></i>
           <input type="text" :value="search" @input="$emit('update:search', $event.target.value)" :placeholder="searchPlaceholder" />
        </div>
        <span style="font-size: 11px; color: var(--pd-text-muted, #64748B);">
          <slot name="counter">Mostrando {{ items !== null ? internalPageItems.length : visibleItems }} de {{ internalTotalItems }} registros</slot>
        </span>
      </div>
    </div>
    
    <div class="pds__table-scroll">
      <table class="pds__table">
        <thead>
          <tr v-if="headers && headers.length">
            <th v-for="h in headers" :key="h.key || h.label" 
                @click="handleSortClick(h)"
                :style="{ cursor: h.sortable ? 'pointer' : 'default', textAlign: h.align || 'left' }"
                :class="{ 'pds__th-sortable': h.sortable }">
              {{ h.label }}
              <i v-if="h.sortable && internalSortKey !== h.key" class="fa-solid fa-sort" style="opacity: 0.3; margin-left: 4px;"></i>
              <i v-if="h.sortable && internalSortKey === h.key && internalSortDir === 'asc'" class="fa-solid fa-sort-up" style="margin-left: 4px; color: #0A5C36;"></i>
              <i v-if="h.sortable && internalSortKey === h.key && internalSortDir === 'desc'" class="fa-solid fa-sort-down" style="margin-left: 4px; color: #0A5C36;"></i>
            </th>
          </tr>
          <slot v-else name="header"></slot>
        </thead>
        <tbody>
          <!-- Modo Inteligente: Tabla procesa Sorting y Paginación internamente -->
          <template v-if="items !== null">
            <slot v-for="(item, index) in internalPageItems" name="row" :item="item" :index="index"></slot>
            <tr v-if="internalPageItems.length === 0">
              <td :colspan="headers.length || 1" class="pds__empty">Sin registros para mostrar</td>
            </tr>
          </template>
          
          <!-- Modo Clásico: Renderizado custom inyectado desde el padre -->
          <slot v-else name="body"></slot>
        </tbody>
      </table>
    </div>
    
    <div class="pds__pagination" v-if="pagination && internalTotalPages > 1">
      <button @click="$emit('update:page', page - 1)" :disabled="page <= 1">Anterior</button>
      <span>Página {{ page }} de {{ internalTotalPages }}</span>
      <button @click="$emit('update:page', page + 1)" :disabled="page >= internalTotalPages">Siguiente</button>
    </div>
  </div>
</template>

<script>
import DashboardSelect from './DashboardSelect.vue';

export default {
  name: 'DashboardTable',
  components: { DashboardSelect },
  props: {
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    showSearch: { type: Boolean, default: true },
    searchPlaceholder: { type: String, default: 'Buscar orden, proveedor o SKU...' },
    search: { type: String, default: '' },
    searchKeys: { type: Array, default: () => [] },
    filterableColumns: { type: Array, default: () => [] },
    
    // Props Modo Inteligente
    items: { type: Array, default: () => null },
    itemsPerPage: { type: Number, default: 10 },
    headers: { type: Array, default: () => [] },
    
    // Props Modo Clásico
    visibleItems: { type: Number, default: 0 },
    totalItems: { type: Number, default: 0 },
    totalPages: { type: Number, default: 1 },
    
    pagination: { type: Boolean, default: false },
    page: { type: Number, default: 1 }
  },
  data() {
    return {
      internalSortKey: '',
      internalSortDir: '',
      internalFilters: {}
    };
  },
  computed: {
    computedFilterableColumns() {
      if (!this.items || !this.filterableColumns || this.filterableColumns.length === 0) return [];
      return this.filterableColumns.map(fk => {
         const options = [...new Set(this.items.map(i => i[fk.key]).filter(Boolean))].sort();
         return {
            key: fk.key,
            label: fk.label || fk.key,
            options
         };
      });
    },
    filteredItems() {
      if (!this.items) return [];
      
      let result = this.items;
      
      for (const [key, val] of Object.entries(this.internalFilters)) {
         if (val) {
            result = result.filter(item => item[key] === val);
         }
      }
      
      if (this.search && this.searchKeys && this.searchKeys.length > 0) {
        const term = this.search.toLowerCase();
        result = result.filter(item => {
          return this.searchKeys.some(key => {
            return item[key] && String(item[key]).toLowerCase().includes(term);
          });
        });
      }
      return result;
    },
    sortedItems() {
      if (!this.filteredItems) return [];
      if (!this.internalSortKey || !this.internalSortDir) return this.filteredItems;
      
      return [...this.filteredItems].sort((a, b) => {
        let va = a[this.internalSortKey];
        let vb = b[this.internalSortKey];
        
        if (this.internalSortKey === 'OrdDate' || this.internalSortKey === 'PromisedDate') {
           const tA = va ? new Date(va.split('/').reverse().join('-')).getTime() : 0;
           const tB = vb ? new Date(vb.split('/').reverse().join('-')).getTime() : 0;
           return this.internalSortDir === 'asc' ? tA - tB : tB - tA;
        }
        
        if (va === undefined || va === null) va = '';
        if (vb === undefined || vb === null) vb = '';
        if (typeof va === 'string' && typeof vb === 'string') {
          va = va.toLowerCase();
          vb = vb.toLowerCase();
        }
        if (va < vb) return this.internalSortDir === 'asc' ? -1 : 1;
        if (va > vb) return this.internalSortDir === 'asc' ? 1 : -1;
        return 0;
      });
    },
    internalTotalItems() {
      return this.items !== null ? this.filteredItems.length : this.totalItems;
    },
    internalTotalPages() {
      if (!this.pagination) return 1;
      if (this.items !== null) return Math.max(1, Math.ceil(this.sortedItems.length / this.itemsPerPage));
      return this.totalPages;
    },
    internalPageItems() {
      if (!this.items) return [];
      if (!this.pagination) return this.sortedItems;
      const start = (this.page - 1) * this.itemsPerPage;
      return this.sortedItems.slice(start, start + this.itemsPerPage);
    }
  },
  methods: {
    handleSortClick(h) {
      if (!h.sortable) return;
      if (this.internalSortKey === h.key) {
        if (this.internalSortDir === 'asc') this.internalSortDir = 'desc';
        else if (this.internalSortDir === 'desc') {
          this.internalSortDir = '';
          this.internalSortKey = '';
        } else this.internalSortDir = 'asc';
      } else {
        this.internalSortKey = h.key;
        this.internalSortDir = 'asc';
      }
      this.$emit('sort', { key: this.internalSortKey, dir: this.internalSortDir });
    }
  }
}
</script>

<style scoped>
/* TABLA BASE */
.pds__table-wrap { display: flex !important; flex-direction: column !important; background: var(--pd-bg, #fff) !important; border: 1px solid var(--pd-border, #E2E8F0) !important; border-radius: 16px !important; overflow: hidden !important; box-shadow: 0 1px 3px rgba(0,0,0,.06) !important; }
.pds__table-head { flex-shrink: 0 !important; display: flex !important; align-items: center !important; justify-content: space-between !important; padding: 14px 20px !important; background: var(--pd-card-bg, #FFFFFF) !important; border-bottom: 1px solid var(--pd-border, #E2E8F0) !important; }
.pds__table-head h3 { font-size: 10px !important; font-weight: 700 !important; color: var(--pd-text, #1E293B) !important; text-transform: uppercase !important; letter-spacing: .6px !important; margin: 0 0 2px 0 !important; }
.pds__table-head p  { font-size: 9px !important; color: var(--pd-text-muted, #64748B) !important; margin: 0 !important; }
.pds__table-head span { font-size: 11px !important; color: var(--pd-text-muted, #64748B) !important; font-weight: 600 !important; white-space: nowrap !important; }

.pds__table-scroll { flex: 1 !important; min-height: 0 !important; overflow-y: auto !important; overflow-x: auto !important; -webkit-overflow-scrolling: touch !important; width: 100% !important; }
.pds__table { width: 100% !important; border-collapse: collapse !important; text-align: left !important; }
.pds__table thead tr { background: var(--pd-table-th-bg, #F1F5F9) !important; }
.pds__table th { padding: 10px 14px !important; font-size: 9px !important; font-weight: 700 !important; color: var(--pd-text-muted, #64748B) !important; text-transform: uppercase !important; letter-spacing: .6px !important; white-space: nowrap !important; text-align: left; transition: color 0.2s; }

/* HOVER MODERNO PARA ORDENAMIENTO */
.pds__table th.pds__th-sortable { user-select: none !important; }
.pds__table th.pds__th-sortable:hover { color: var(--pd-text, #1E293B) !important; }
.pds__table th.pds__th-sortable i { transition: color 0.2s, opacity 0.2s; }
.pds__table th.pds__th-sortable:hover i { opacity: 0.8 !important; color: var(--pd-green, #0A5C36) !important; }

.pds__table td { padding: 10px 14px !important; font-size: 11px !important; color: var(--pd-text, #334155) !important; border-bottom: 1px solid var(--pd-table-th-bg, #F1F5F9) !important; vertical-align: middle !important; }
.pds__table tbody tr:hover { background: var(--pd-sidebar-bg, #F8FAFC) !important; }

/* PAGINACIÓN */
.pds__pagination { flex-shrink: 0 !important; display: flex !important; align-items: center !important; justify-content: space-between !important; padding: 10px 20px !important; background: var(--pd-card-bg, #FFFFFF) !important; border-top: 1px solid var(--pd-border, #E2E8F0) !important; }
.pds__pagination button { padding: 5px 14px !important; background: var(--pd-bg, #fff) !important; border: 1px solid var(--pd-border, #E2E8F0) !important; border-radius: 8px !important; font-size: 11px !important; font-weight: 600 !important; color: var(--pd-text, #475569) !important; cursor: pointer !important; font-family: 'Inter', sans-serif !important; transition: all 0.2s; }
.pds__pagination button:hover:not(:disabled) { background: var(--pd-table-th-bg, #F1F5F9) !important; }
.pds__pagination button:disabled { opacity: .3 !important; cursor: not-allowed !important; }
.pds__pagination span { font-size: 11px !important; color: var(--pd-text-muted, #64748B) !important; }

/* BÚSQUEDA */
.pd-table-search { position: relative; width: 100%; max-width: 250px; }
.pd-table-search i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); font-size: 12px; color: var(--pd-text-muted, #94A3B8); pointer-events: none; }
.pd-table-search input { width: 100%; height: 32px; padding: 0 12px 0 32px; border: 1px solid var(--pd-border, #E2E8F0); border-radius: 8px; font-size: 11px; background: var(--pd-bg, #fff); color: var(--pd-text, #1E293B); outline: none; transition: border-color 0.2s; font-family: 'Inter', sans-serif; }
.pd-table-search input:focus { border-color: var(--pd-green, #0A5C36); box-shadow: 0 0 0 2px rgba(10,92,54,0.1); }
.pd-table-search input::placeholder { color: var(--pd-text-muted, #94A3B8); }


/* V-DEEP PARA ESTILOS DE CELDAS INYECTADAS DESDE EL PADRE */
::v-deep .pds__empty { text-align: center !important; padding: 32px !important; color: var(--pd-text-muted, #94A3B8) !important; font-size: 12px !important; }
::v-deep .pds__badge-oc { background: var(--pd-table-th-bg, #F1F5F9) !important; border: 1px solid var(--pd-border, #E2E8F0) !important; border-radius: 6px !important; padding: 2px 6px !important; font-size: 10px !important; font-weight: 700 !important; font-family: monospace !important; white-space: nowrap !important; color: var(--pd-text, #334155) !important;}
::v-deep .pds__estado { padding: 3px 8px !important; border-radius: 20px !important; font-size: 9px !important; font-weight: 700 !important; text-transform: uppercase !important; letter-spacing: .4px !important; white-space: nowrap !important; }
::v-deep .pds__estado--closed   { background: #D1FAE5 !important; color: #065F46 !important; }
::v-deep .pds__estado--open     { background: #FEF3C7 !important; color: #92400E !important; }
::v-deep .pds__estado--canceled { background: #FEE2E2 !important; color: #991B1B !important; }
::v-deep .pds__mono  { font-family: monospace !important; }
::v-deep .pds__desc  { max-width: 220px !important; white-space: nowrap !important; overflow: hidden !important; text-overflow: ellipsis !important; }
::v-deep .text-right  { text-align: right !important; }
::v-deep .text-center { text-align: center !important; }
</style>
